// Feature 012: template selection and readiness for clients.
import { useEffect, useState, type FC } from 'react';
import { SelectInput, required, useDataProvider, useGetList, useRecordContext } from 'react-admin';
import { Alert, Box, Chip, CircularProgress, List, ListItem, Typography } from '@mui/material';
import type { LogtoDataProvider } from '../../dataProvider';
import type { BuildReadiness, Client, Template } from '../../types';
import { apiErrorMessage } from '../templateSettings';

/** Template select; only active templates are offered (plus the client's current one). */
export const TemplateSelectInput = () => {
  const record = useRecordContext<Client>();
  const { data: templates, isLoading } = useGetList<Template>('templates', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'key', order: 'ASC' },
  });
  const choices = (templates || [])
    .filter((t) => t.active || t.key === record?.templateKey)
    .map((t) => ({ id: t.key, name: `${t.name} (${t.key})${t.active ? '' : ' — неактивен'}` }));

  return (
    <SelectInput
      source="templateKey"
      label="Шаблон приложения"
      choices={choices}
      isLoading={isLoading}
      validate={required()}
      helperText="Из какого шаблона собирается приложение. Настройки других шаблонов сохраняются."
      sx={{ minWidth: 320 }}
    />
  );
};

/** Template key + platform version chip. */
// `label` is read by Datagrid / SimpleShowLayout for the column/field title.
export const TemplateChip: FC<{ label?: string }> = () => {
  const record = useRecordContext<Client>();
  if (!record) return null;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={record.currentCoreVersion ? `${record.templateKey} · ${record.currentCoreVersion}` : record.templateKey}
    />
  );
};

/** Pre-build check for the client's current template. */
export const BuildReadinessPanel: FC<{ label?: string }> = () => {
  const record = useRecordContext<Client>();
  const dataProvider = useDataProvider<LogtoDataProvider>();
  const [readiness, setReadiness] = useState<BuildReadiness | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!record?.id) return;
    dataProvider
      .getBuildReadiness(String(record.id))
      .then(setReadiness)
      .catch((e) => setError(apiErrorMessage(e, 'Не удалось проверить готовность')));
  }, [record?.id, record?.templateKey, dataProvider]);

  if (!record) return null;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!readiness) return <CircularProgress size={20} />;

  if (readiness.ready) {
    return <Alert severity="success">Готов к сборке (шаблон {readiness.templateKey})</Alert>;
  }
  return (
    <Alert severity="warning">
      <Typography variant="body2">Сборка на шаблоне {readiness.templateKey} не запустится, пока не исправлено:</Typography>
      <Box component={List} dense sx={{ py: 0 }}>
        {readiness.missing.map((m) => (
          <ListItem key={m.field + m.code} sx={{ py: 0 }}>
            <code>{m.field}</code>&nbsp;— {m.message}
          </ListItem>
        ))}
      </Box>
    </Alert>
  );
};
